import * as React from 'react'
import { Logger } from 'src/3rds/cube'

import { makeStyles } from 'tss-react/mui'

import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import SearchIcon from '@mui/icons-material/Search'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'

import {
    DashboardsScope,
    OrdemParceiros,
    PARCEIROS_POR_PAGINA,
    ROTULO_SITUACAO,
    SituacaoParceiro
} from '../Dashboards.scopes'
import { percentual } from '../Negocios.graficos'
import { opcaoAtivosPorDia, opcaoFaixas, pareceAutomatizado } from '../Parceiros.graficos'
import { Grafico, Numero, useCardStyles } from './Componentes'
import { EChart } from './EChart'

const LOG = Logger.get('Parceiros-VIEW')

const numero = (n: number) => n.toLocaleString('pt-BR')
const data = (iso?: string) => (iso ? new Date(iso).toLocaleDateString('pt-BR') : '—')

const ROTULO_ORDEM: Record<OrdemParceiros, string> = {
    consultas: 'Mais consultas',
    cadastros: 'Mais negócios cadastrados',
    taxa: 'Maior taxa de cadastro',
    ultima: 'Consulta mais recente',
    carteira: 'Maior carteira',
    nome: 'Nome'
}

const useStyles = makeStyles()((theme) => ({
    blocos: {
        display: 'flex',
        flexDirection: 'column',
        rowGap: theme.spacing(2.5)
    },
    numeros: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: theme.spacing(2)
    },
    lado: {
        display: 'flex',
        gap: theme.spacing(2.5),
        flexWrap: 'wrap',
        '& > *': {
            flex: '1 1 420px',
            minWidth: 0
        }
    },
    filtros: {
        display: 'flex',
        gap: theme.spacing(1.5),
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: theme.spacing(1.5)
    },
    automatizado: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        color: theme.palette.warning.dark
    }
}))

/** Dashboard de parceiros (E18): quem usa a LIA e com que resultado? */
export function ParceirosPainel({ scope }: { scope: DashboardsScope }) {
    const { classes } = useStyles()
    const { classes: cards } = useCardStyles()
    const parceiros = scope.parceiros!
    const periodo = parceiros.periodo

    const opcoes = React.useMemo(
        () => ({
            porDia: opcaoAtivosPorDia(parceiros.porDia, periodo),
            faixas: opcaoFaixas(parceiros.faixas)
        }),
        [parceiros, periodo]
    )

    const emitBusca = (evt: React.ChangeEvent<HTMLInputElement>) =>
        scope.onBuscaParceiroChange(evt.target.value).catch(LOG.caught)
    const emitSituacao = (evt: React.ChangeEvent<HTMLInputElement>) =>
        scope.onSituacaoParceiroChange(evt.target.value as SituacaoParceiro).catch(LOG.caught)
    const emitOrdem = (evt: React.ChangeEvent<HTMLInputElement>) =>
        scope.onOrdemParceirosChange(evt.target.value as OrdemParceiros).catch(LOG.caught)
    const emitPagina = (_evt: unknown, pagina: number) => scope.onRankingPaginaChange(pagina).catch(LOG.caught)

    return (
        <Box className={classes.blocos}>
            <Box className={classes.numeros}>
                <Numero
                    titulo="Parceiros que consultaram"
                    valor={numero(parceiros.ativos)}
                    nota={`de ${numero(parceiros.cadastrados)} cadastrados (${percentual(
                        parceiros.ativos,
                        parceiros.cadastrados
                    )})`}
                />
                <Numero
                    titulo="Consultas no período"
                    valor={numero(parceiros.consultas)}
                    nota={`Os 10 que mais consultaram fizeram ${percentual(
                        parceiros.fatiaTop10 * parceiros.consultas,
                        parceiros.consultas
                    )}`}
                />
                <Numero
                    titulo="Negócios cadastrados pela LIA"
                    valor={numero(parceiros.cadastros)}
                    nota={`${percentual(parceiros.cadastros, parceiros.consultas)} das consultas · ${numero(
                        parceiros.renovacoes
                    )} renovações`}
                />
                <Numero
                    titulo="Parados há mais de 30 dias"
                    valor={numero(parceiros.parados)}
                    nota={`${numero(parceiros.nuncaConsultaram)} cadastrados nunca consultaram`}
                />
            </Box>

            <Box className={classes.lado}>
                <Grafico titulo="Parceiros que consultaram, por dia">
                    <EChart option={opcoes.porDia} ariaLabel="Parceiros distintos que consultaram por dia" />
                </Grafico>
                <Grafico
                    titulo="Quanto cada parceiro consulta"
                    nota="Parceiros por faixa de consultas no período. Passe o mouse para ver as consultas de cada faixa."
                >
                    <EChart option={opcoes.faixas} ariaLabel="Parceiros por faixa de consultas no período" />
                </Grafico>
            </Box>

            <Card className={cards.card}>
                {scope.carregandoRanking && <LinearProgress />}
                <CardContent>
                    <Typography className={cards.tituloCard}>Parceiros</Typography>
                    <Typography className={cards.nota}>
                        Consultas do período, última consulta de todo o histórico e negócios em andamento de que cada um
                        é dono hoje.
                    </Typography>
                    <Box className={classes.filtros}>
                        <TextField
                            size="small"
                            sx={{ flex: '1 1 300px', maxWidth: 420 }}
                            placeholder="Buscar por nome, e-mail ou telefone"
                            value={scope.buscaParceiro}
                            onChange={emitBusca}
                            inputProps={{ 'aria-label': 'Buscar parceiro' }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <TextField
                            select
                            size="small"
                            label="Situação"
                            value={scope.situacaoParceiro}
                            onChange={emitSituacao}
                            sx={{ minWidth: 220 }}
                        >
                            {(Object.keys(ROTULO_SITUACAO) as SituacaoParceiro[]).map((s) => (
                                <MenuItem key={s} value={s}>
                                    {ROTULO_SITUACAO[s]}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="Ordenar por"
                            value={scope.ordemParceiros}
                            onChange={emitOrdem}
                            sx={{ minWidth: 220 }}
                        >
                            {(Object.keys(ROTULO_ORDEM) as OrdemParceiros[]).map((o) => (
                                <MenuItem key={o} value={o}>
                                    {ROTULO_ORDEM[o]}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                    <TableContainer sx={{ mt: 1 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Parceiro</TableCell>
                                    <TableCell align="right">Consultas</TableCell>
                                    <TableCell align="right">Cadastrou</TableCell>
                                    <TableCell align="right">Taxa</TableCell>
                                    <TableCell align="right">De madrugada</TableCell>
                                    <TableCell>Última consulta</TableCell>
                                    <TableCell align="right">Abordagem</TableCell>
                                    <TableCell align="right">Reserva ou Cota</TableCell>
                                    <TableCell align="right">Cliente</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {scope.ranking.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={9}>Nenhum parceiro encontrado.</TableCell>
                                    </TableRow>
                                )}
                                {scope.ranking.map((p) => (
                                    <TableRow key={p.ownerId}>
                                        <TableCell>
                                            {pareceAutomatizado(p) ? (
                                                <Tooltip title="Consulta também de madrugada, em volume: pode ser uso automatizado">
                                                    <span className={classes.automatizado}>
                                                        <SmartToyOutlinedIcon fontSize="small" />
                                                        {p.nome}
                                                    </span>
                                                </Tooltip>
                                            ) : (
                                                p.nome
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{numero(p.consultas)}</TableCell>
                                        <TableCell align="right">{numero(p.cadastros)}</TableCell>
                                        <TableCell align="right">
                                            {p.taxa == null ? '—' : percentual(p.cadastros, p.consultas)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {p.madrugada == null
                                                ? '—'
                                                : percentual(p.madrugada * p.consultas, p.consultas)}
                                        </TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{data(p.ultimaConsulta)}</TableCell>
                                        <TableCell align="right">{numero(p.abordagem)}</TableCell>
                                        <TableCell align="right">{numero(p.reserva)}</TableCell>
                                        <TableCell align="right">{numero(p.cliente)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={scope.rankingTotal}
                        page={scope.rankingPagina}
                        onPageChange={emitPagina}
                        rowsPerPage={PARCEIROS_POR_PAGINA}
                        rowsPerPageOptions={[PARCEIROS_POR_PAGINA]}
                        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${numero(count)}`}
                    />
                </CardContent>
            </Card>
        </Box>
    )
}

export default ParceirosPainel
